import { jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import clsx from 'clsx';
import { useLazyInput } from '../hooks/use-lazy-input.js';
import { TextareaPrimitive } from '../textarea-primitive/textarea-primitive.js';
import { getFlags } from '../utils/flags.js';
import { textareaAlt, textarea, large, rootAlt, root } from './textarea.module.css.js';

/**
 * A styled text Textarea component.
 *
 * @see [Figma Design file](https://staging.figma.com/design/N0izGT6Y7WwEOJABqPXBCv/!-UI3-Library?node-id=790-34498)
 * @see https://fpl.figma.design/components/inputs/textarea/
 * @compose { "standalone": true }
 */
const Textarea = Object.assign(
// eslint-disable-next-line react/display-name -- False positive because of the Object.assign
forwardRef(({ size, rows, ...props }, ref) => (jsx(TextareaPrimitive, { "data-fpl-component": "", ...props, ref: ref, rows: rows ?? (size === 'lg' ? 6 : 3), className: clsx(getFlags().fpl_input_style_update ? textareaAlt : textarea, {
        [large]: size === 'lg',
    }) }))), { Root, Lazy });
Textarea.displayName = 'Textarea';
/**
 * When a Textarea should visually contain other elements, `Textarea.Root` can
 * be used as the container, and `Textarea` as the typeable area.
 *
 * @compose { "root": true }
 */
function Root({ className, ...props }) {
    return (jsx(TextareaPrimitive.Root, { "data-fpl-component": "", ...props, className: clsx(getFlags().fpl_input_style_update ? rootAlt : root, className) }));
}
Root.displayName = 'Textarea.Root';
/**
 * Make a Textarea keep track of its own state for a short time while editing. Intended for controlled
 * textareas that may be slow to update, due to the state update triggering a big re-render.
 *
 * Lazy textareas should be preferred when the Textarea does not need to modify the value in any way. If
 * the value needs to be modified (e.g. trimming whitespace), then the Textarea should not use this
 * hook. Instead it can use a regular Textarea with a controlled value, or defer triggering onChange
 * to a later time, such as on blur.
 *
 * @note Uncontrolled textareas do not benefit from this as they don't trigger state updates as the
 *   user types.
 *
 * Must be used on a controlled Textarea, so value and onChange are required.
 * @compose { "standalone": true }
 */
function Lazy(propsIn) {
    const props = useLazyInput(propsIn);
    return jsx(Textarea, { "data-fpl-component": "", ...props });
}
Lazy.displayName = 'Textarea.Lazy';

export { Textarea };
//# sourceMappingURL=textarea.js.map
